import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { AuthLayout } from '../components/AuthLayout'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { getSupabase } from '../lib/supabase'

export function SignUpPage() {
  const { session, loading } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checkEmail, setCheckEmail] = useState(false)

  if (!loading && session) return <Navigate to="/" replace />

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (password.length < 8) {
      setError('Please choose a password with at least 8 characters.')
      return
    }

    setSubmitting(true)
    setError(null)

    const { data, error: signUpError } = await getSupabase().auth.signUp({
      email: email.trim(),
      password,
      options: { emailRedirectTo: `${window.location.origin}/` },
    })

    if (signUpError) {
      setError(signUpError.message)
      setSubmitting(false)
      return
    }

    // With confirmations enabled Supabase returns no session → tell the user
    // to check their inbox. Otherwise they are signed in immediately.
    if (data.session) {
      navigate('/', { replace: true })
    } else {
      setCheckEmail(true)
      setSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title={checkEmail ? 'Check your inbox' : 'Create your account'}
      subtitle={
        checkEmail
          ? `We sent a confirmation link to ${email.trim()}. Follow it to activate your account.`
          : 'Sign up to access your nMach workspace.'
      }
      footer={
        <p>
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-cobalt underline-offset-4 hover:underline">
            Sign in
          </Link>
        </p>
      }
    >
      {!checkEmail && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <Field
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label="Password"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={8}
            hint="At least 8 characters."
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={error}
          />
          <Button type="submit" size="lg" fullWidth loading={submitting}>
            Create account
          </Button>
        </form>
      )}

      {checkEmail && (
        <Button type="button" variant="secondary" size="lg" fullWidth onClick={() => navigate('/login')}>
          Back to sign in
        </Button>
      )}
    </AuthLayout>
  )
}
